import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { Skeleton } from 'primeng/skeleton';
import { Tag } from 'primeng/tag';
import { Button } from 'primeng/button';
import {
  isUnavailableAccountTransaction,
  type AccountTransactionRow,
  type TransactionDatePreset,
  type TransactionStatusFilter,
} from '../account-transactions.model';
import {
  accountDetailKind,
  canFilterTransactionStatus,
  canSearchAccountTransactions,
  defaultTransactionDatePreset,
  inventoryTypeToPathType,
} from '../account-path-type';
import {
  formatAccountDate,
  formatTransactionAmount,
  transactionStatusSeverity,
} from '../accounts-display';
import {
  DEBT_TRANSACTION_STATUS_OPTIONS,
  displayTransactionDescription,
  filterTransactions,
  LOAN_TRANSACTION_STATUS_OPTIONS,
  paginateTransactions,
  resolveTransactionQuery,
  TRANSACTION_DATE_OPTIONS,
} from './account-transactions.filter';
import { AccountTransactionsStore } from './account-transactions.store';

@Component({
  selector: 'bn-account-transactions',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, DatePicker, Select, Skeleton, Tag, Button],
  templateUrl: './account-transactions.component.html',
  styleUrl: './account-transactions.component.scss',
})
export class AccountTransactionsComponent {
  readonly store = inject(AccountTransactionsStore);

  readonly guid = input.required<string>();
  readonly accountId = input.required<string>();
  readonly accountType = input.required<string>();

  readonly search = signal('');
  readonly datePreset = linkedSignal(() => defaultTransactionDatePreset(this.accountType()));
  readonly customFrom = signal<Date | null>(null);
  readonly customTo = signal<Date | null>(null);
  readonly status = signal<TransactionStatusFilter>('all');
  readonly page = signal(1);

  readonly dateOptions = TRANSACTION_DATE_OPTIONS;
  readonly statusOptions = computed(() =>
    accountDetailKind(this.accountType()) === 'loan'
      ? LOAN_TRANSACTION_STATUS_OPTIONS
      : DEBT_TRANSACTION_STATUS_OPTIONS
  );
  readonly formatDate = formatAccountDate;
  readonly formatAmount = formatTransactionAmount;
  readonly displayDescription = displayTransactionDescription;
  readonly statusSeverity = transactionStatusSeverity;
  readonly isUnavailable = isUnavailableAccountTransaction;

  readonly transactionTrackKey = (row: AccountTransactionRow): string | number =>
    isUnavailableAccountTransaction(row) ? row.rowKey : row.transactionId;

  readonly showSearch = computed(() => canSearchAccountTransactions(this.accountType()));
  readonly showStatus = computed(() => canFilterTransactionStatus(this.accountType()));
  readonly showCustomRange = computed(() => this.datePreset() === 'custom');

  readonly query = computed(() =>
    resolveTransactionQuery(
      this.datePreset(),
      this.customFrom(),
      this.customTo(),
      this.status(),
      this.showStatus()
    )
  );

  readonly filtered = computed(() =>
    filterTransactions(this.store.rows(), { search: this.search() })
  );

  readonly paged = computed(() => paginateTransactions(this.filtered(), this.page()));

  constructor() {
    effect(() => {
      const guid = this.guid();
      const id = this.accountId();
      const pathType = inventoryTypeToPathType(this.accountType());
      const query = this.query();
      if (!guid || !id || !pathType) {
        return;
      }
      if (query.kind !== 'ready') {
        untracked(() => this.store.reset());
        return;
      }
      untracked(() => {
        this.page.set(1);
        void this.store.load(guid, pathType, id, query.options);
      });
    });
  }

  onSearchChange(value: string): void {
    this.search.set(value);
    this.page.set(1);
  }

  onDatePresetChange(value: TransactionDatePreset): void {
    this.datePreset.set(value);
    if (value !== 'custom') {
      this.customFrom.set(null);
      this.customTo.set(null);
    }
    this.page.set(1);
  }

  onCustomFromChange(value: Date | null): void {
    this.customFrom.set(value);
    this.page.set(1);
  }

  onCustomToChange(value: Date | null): void {
    this.customTo.set(value);
    this.page.set(1);
  }

  onStatusChange(value: TransactionStatusFilter): void {
    this.status.set(value);
    this.page.set(1);
  }

  prevPage(): void {
    this.page.set(Math.max(1, this.paged().page - 1));
  }

  nextPage(): void {
    this.page.set(Math.min(this.paged().pageCount, this.paged().page + 1));
  }
}
